/**
 * Утилиты для OutcomeScheme — human-readable лейблы, описания, иконки/цвета.
 *
 * Используется в конструкторе EventType, формах ставки/результата
 * и везде, где нужно отобразить схему исходов.
 */

import { OutcomeScheme } from '../../../models/eventType';

// ===================================================================================================================
// = Лейблы и описания
// ===================================================================================================================

const SCHEME_LABELS = Object.freeze({
  [OutcomeScheme.SCORE_BASED]: 'Счётный исход',
  [OutcomeScheme.WINNER_ONLY]: 'Только победитель',
  [OutcomeScheme.WINNER_WITH_METHOD]: 'Победитель + способ',
  [OutcomeScheme.PLACEMENT]: 'Места (1/2/3)',
  [OutcomeScheme.INDIVIDUAL_SCORE]: 'Место + балл',
  [OutcomeScheme.CUSTOM]: 'Произвольные исходы',
});

const SCHEME_DESCRIPTIONS = Object.freeze({
  [OutcomeScheme.SCORE_BASED]: 'Счёт home:away + код исхода (P1/X/P2 и т.д.). Подходит для футбола, хоккея, баскетбола.',
  [OutcomeScheme.WINNER_ONLY]: 'Выбор победителя из списка участников. Без ничьей и счёта.',
  [OutcomeScheme.WINNER_WITH_METHOD]: 'Выбор победителя и способа победы (KO, SUB, DECISION). Для единоборств.',
  [OutcomeScheme.PLACEMENT]: 'Распределение мест (1-е, 2-е, 3-е). Для гонок, турниров.',
  [OutcomeScheme.INDIVIDUAL_SCORE]: 'Место + балльная оценка. Для фигурного катания, гимнастики.',
  [OutcomeScheme.CUSTOM]: 'Произвольные текстовые исходы. Устаревший режим (legacy).',
});

// ===================================================================================================================
// = Публичный API
// ===================================================================================================================

/**
 * Возвращает human-readable лейбл схемы исходов.
 */
export function outcomeSchemeLabel(scheme) {
  return SCHEME_LABELS[scheme] || scheme;
}

/**
 * Возвращает описание схемы исходов.
 */
export function outcomeSchemeDescription(scheme) {
  return SCHEME_DESCRIPTIONS[scheme] || '';
}

/**
 * Список всех OutcomeScheme для select-а.
 */
export function getOutcomeSchemeOptions() {
  return Object.values(OutcomeScheme).map((scheme) => ({
    value: scheme,
    label: outcomeSchemeLabel(scheme),
    hint: outcomeSchemeDescription(scheme),
  }));
}

/**
 * Определяет, нужна ли DrawPolicy для данной схемы.
 */
export function isDrawPolicyRequired(scheme) {
  return scheme === OutcomeScheme.SCORE_BASED;
}

/**
 * Определяет, нужен ли список winMethods для данной схемы.
 */
export function isWinMethodsRequired(scheme) {
  return scheme === OutcomeScheme.WINNER_WITH_METHOD;
}

/**
 * Определяет, нужны ли параметры балльной системы для данной схемы.
 */
export function isIndividualScoreParamsRequired(scheme) {
  return scheme === OutcomeScheme.INDIVIDUAL_SCORE;
}

/**
 * Определяет, нужна ли форма счёта (home:away) для ставки/результата.
 */
export function isScoreBasedScheme(scheme) {
  return scheme === OutcomeScheme.SCORE_BASED;
}

/**
 * Определяет, является ли схема legacy (CUSTOM).
 */
export function isCustomScheme(scheme) {
  return scheme === OutcomeScheme.CUSTOM;
}

/**
 * Возвращает emoji/иконку для схемы.
 */
export function outcomeSchemeIcon(scheme) {
  switch (scheme) {
    case OutcomeScheme.SCORE_BASED: return '⚽';
    case OutcomeScheme.WINNER_ONLY: return '🏆';
    case OutcomeScheme.WINNER_WITH_METHOD: return '🥊';
    case OutcomeScheme.PLACEMENT: return '🥇';
    case OutcomeScheme.INDIVIDUAL_SCORE: return '⛸️';
    case OutcomeScheme.CUSTOM: return '✏️';
    default: return '📋';
  }
}
